import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfoContext } from "../components/layout/AppearanceProvider.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";
import { useToast } from "../hooks/useToast.js";
import { createUser, getCurrentUser, login } from "../services/authService.js";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { VALIDATION_RULES } from "../utils/validation/validationRules.js";

const teamMemberValidation = {
  name: VALIDATION_RULES.full_name,
  phone: VALIDATION_RULES.phone_required,
  role: VALIDATION_RULES.requirement,
  newPassword: {
    required: "Password is required",
    pattern: {
      regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
      message:
        "Password must be at least 6 characters with uppercase, lowercase, and number",
    },
  },
  confirmPassword: {
    required: "Please confirm your password",
    matchField: "newPassword",
  },
};

export default function AddTeamMember() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    form: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    setFieldValue,
  } = useFormValidation(
    {
      name: "",
      phone: "",
      role: "business_admin",
      newPassword: "",
      confirmPassword: "",
    },
    teamMemberValidation
  );

  const { name, phone, role, newPassword, confirmPassword } = formData;

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  const { email, otpSent, otpVerified, reset } = useInfoContext();

  useEffect(() => {
    if (!email || !otpSent || !otpVerified) {
      navigate("/send-otp");
    }
  }, [email, otpSent, otpVerified, navigate]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser?.success && currentUser.user) {
          const role = currentUser.user.user_role;
          setCurrentUserRole(role);
          setFieldValue(
            "role",
            role === "business_admin" ? "sales_person" : "business_admin"
          );
          return;
        }
      } catch (err) {
         toast({
          variant: "destructive",
          title: "getCurrentUser failed, assuming first user",
        });
      }
      setCurrentUserRole(null);
      setFieldValue("role", "business_admin");
    };

    fetchUserRole();
  }, []);

  const handleAddNewMember = async () => {
    if (!email) {
      toast({
        title: "Missing email",
        description: "Email is required.",
        variant: "destructive",
      });
      return;
    }

    if (!validateAll()) {
      toast({
        title: "Validation Error",
        description: "Invalid credentials.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAddingMember(true);
      const res = await createUser({
        name,
        email,
        contact_number: phone,
        user_role: role,
        password: newPassword,
      });

      if (!res?.success) {
        toast({
          title: "Failed to add new member",
          description: res?.error || "Some error occurred.",
          variant: "destructive",
        });
        return;
      }

      if (role === "sales_person") {
        toast({
          title: "Success",
          description: "Sales Person created successfully!",
        });
        reset();
        navigate("/team");
      } else {
        try {
          const authResponse = await login({ email, password: newPassword });

          if (authResponse?.success && role === "business_admin") {
            toast({
              title: "Success",
              description: "Business Admin created successfully!",
            });
            reset();
            navigate("/add-company", { replace: true });
          } else {
            toast({
              title: "Failed to add new member",
              description: res?.error || "Some error occurred.",
              variant: "destructive",
            });
          }
        } catch (err) {
          toast({
            title: "Error",
            description: err.message || "Something went wrong",
            variant: "destructive",
          });
        }
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Unable to set password",
        variant: "destructive",
      });
    } finally {
      setIsAddingMember(false);
    }
  };

  function handleGoBack() {
    navigate("/send-otp");
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6  ">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleGoBack}
          className="rounded-full hover:bg-gray-200 transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-3xl font-semibold text-gray-800">Add New Member</h1>
      </div>

      <form
        className="space-y-3 "
        onSubmit={(e) => e.preventDefault()}
      >
        <Card className="pb-4">
          <CardHeader className="p-4">
            <CardTitle className="text-lg font-medium text-gray-700">
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className=" space-y-2">
            {/* Full Name */}
            <div className="flex flex-col">
              <Label>Full Name *</Label>
              <Input
                id="name"
                name="name"
                placeholder="Enter full name"
                value={name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`mt-1 h-8 text-xs border ${
                  errors.name ? "border-red-500" : "border-gray-300"
                } rounded-lg `}
              />

              {touched.name && errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="flex flex-col">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@nipralo.com"
                value={email}
                disabled
                className="mt-1  h-8 text-xs border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>

            {/* Phone */}
            <div className="flex flex-col">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="9876543210"
                value={phone}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="new-password"
                maxLength={10}
                className={`mt-1 h-8 text-xs border ${
                  errors.phone ? "border-red-500" : "border-gray-300"
                } rounded-lg `}
              />
              {touched.phone && errors.phone && (
                <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
              )}
            </div>

            {/* Role */}
            <div className="flex flex-col">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setFieldValue("role", value)}
              >
                <SelectTrigger className="mt-1 h-8 text-xs border border-gray-300 rounded-lg ">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value="business_admin"
                    disabled={currentUserRole === "business_admin"}
                  >
                    Business Admin
                  </SelectItem>
                  <SelectItem
                    value="sales_person"
                    disabled={currentUserRole === null}
                  >
                    Sales Person
                  </SelectItem>
                </SelectContent>
              </Select>
              {touched.role && errors.role && (
                <p className="mt-1 text-xs text-red-500">{errors.role}</p>
              )}
            </div>

            {/* New Password */}

            <div className="relative flex flex-col w-full">
              <Label htmlFor="newPassword">Set Password</Label>

              {/* Password Input */}
              <div className="relative flex items-center">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Create password"
                  value={newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`mt-1 h-8 text-sm pr-10 border ${
                    errors.newPassword ? "border-red-500" : "border-gray-300"
                  } rounded-lg w-full `}
                  autoComplete="new-password"
                />

                {/* Toggle Visibility Button */}
                <button
                  type="button"
                  className="absolute right-3 text-gray-400 hover:text-gray-600 flex items-center justify-center"
                  onClick={() => setShowNewPassword((p) => !p)}
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Error Message */}
              {touched.newPassword && errors.newPassword && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative flex flex-col w-full">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative flex items-center">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`mt-1 h-8 text-sm pr-10 border ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  } rounded-lg w-full `}
                />

                <button
                  type="button"
                  className="absolute right-3 text-gray-400 hover:text-gray-600 flex items-center justify-center"
                  onClick={() => setShowConfirmPassword((p) => !p)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="button"
            className=" text-xs bg-gradient-to-r from-teal-400 to-teal-600 text-white mt-0 py-0 h-8 rounded-full hover:scale-105 transition"
            onClick={handleAddNewMember}
            disabled={isAddingMember}
          >
            {isAddingMember ? "Adding New Member..." : "Add New Member"}
          </Button>
        </div>
      </form>
    </div>
  );
}
